/**
 * ADR-0024: installed apps live on the device.
 *
 * The server lists the exact files of each installed app's current version
 * (GET /api/apps/bundles). When a version is new, the shell downloads every file,
 * checks it against its SHA-256 and only then switches to it, so an app is never
 * half-updated. In the native app the files sit in the WebView's Cache Storage and
 * the app opens from them without the network. On the web the same download warms
 * the browser cache for the versioned URLs, which never change.
 */
import { api, apiBase, resolveUrl } from "./api";
import { isNative } from "./platform";
import { load, save } from "./storage";

export interface BundleFile {
  path: string;
  size: number;
  sha256: string;
}

export interface InstalledBundle {
  app_id: string;
  name: string;
  version: string;
  hash: string;
  base_url: string;
  entry: string;
  size: number;
  files: BundleFile[];
  /** Event patterns this app may hear (ADR-0025). */
  events: string[];
  installed_at?: string;
}

const RECORDS = "omni.installed";
const CACHE = "omni-apps-v1";
// Native opens installed apps from the device. A development build of the web shell can opt in
// for testing (Settings → On this device); production web can't, its CSP blocks inline scripts.
export const opensLocally = () => isNative || (import.meta.env.DEV && load<boolean>("omni.localApps", false));

const docs = new Map<string, string>();
let syncing: Promise<SyncResult> | null = null;
const listeners = new Set<() => void>();

export interface SyncResult {
  updated: string[];
  removed: string[];
  failed: string[];
}

function records(): Record<string, InstalledBundle> {
  return load<Record<string, InstalledBundle>>(`${RECORDS}:${apiBase()}`, {});
}

function saveRecords(next: Record<string, InstalledBundle>) {
  save(`${RECORDS}:${apiBase()}`, next);
  listeners.forEach((fn) => fn());
}

export function installed(appId: string): InstalledBundle | null {
  return records()[appId] ?? null;
}

export function installedAll(): InstalledBundle[] {
  return Object.values(records());
}

export function onInstalledChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const fileUrl = (b: Pick<InstalledBundle, "base_url">, path: string) => resolveUrl(`${b.base_url}${path}`)!;

async function sha256(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

async function cache(): Promise<Cache | null> {
  try {
    return "caches" in window ? await caches.open(CACHE) : null;
  } catch {
    return null; // e.g. an insecure context
  }
}

/** Download one version completely and verify it; nothing changes if any file fails. */
async function download(b: InstalledBundle): Promise<void> {
  const store = await cache();
  const fetched: [string, Response][] = [];
  for (const f of b.files) {
    const url = fileUrl(b, f.path);
    const res = await fetch(url, { cache: "force-cache" });
    if (!res.ok) throw new Error(`${b.app_id}: ${f.path} (${res.status})`);
    const body = await res.arrayBuffer();
    if ((await sha256(body)) !== f.sha256) throw new Error(`${b.app_id}: ${f.path} failed its integrity check`);
    fetched.push([url, new Response(body, { headers: { "Content-Type": res.headers.get("Content-Type") ?? "application/octet-stream" } })]);
  }
  // The web only needs the warm HTTP cache; the device keeps its own copy
  if (store && opensLocally()) for (const [url, res] of fetched) await store.put(url, res);
}

/** Is this version's copy complete on the device? (It can be evicted, or never made on the web.) */
async function present(b: InstalledBundle): Promise<boolean> {
  if (!opensLocally()) return true;
  const store = await cache();
  if (!store) return true;
  for (const f of b.files) if (!(await store.match(fileUrl(b, f.path)))) return false;
  return true;
}

async function forget(b: InstalledBundle): Promise<void> {
  const store = await cache();
  if (store) for (const f of b.files) await store.delete(fileUrl(b, f.path));
  docs.delete(b.hash);
}

/**
 * Bring the device in line with the server: download new or updated apps,
 * remove uninstalled ones. Safe to call often; runs one at a time.
 */
export function syncBundles(): Promise<SyncResult> {
  syncing ??= (async () => {
    const result: SyncResult = { updated: [], removed: [], failed: [] };
    try {
      const { items } = await api<{ items: InstalledBundle[] }>("/api/apps/bundles");
      const current = records();
      const next: Record<string, InstalledBundle> = {};
      for (const b of items) {
        const have = current[b.app_id];
        if (have && have.hash === b.hash && (await present(have))) {
          next[b.app_id] = { ...have, events: b.events, name: b.name };
          continue;
        }
        try {
          await download(b);
          next[b.app_id] = { ...b, installed_at: new Date().toISOString() };
          if (have && have.hash !== b.hash) await forget(have);
          result.updated.push(b.app_id);
        } catch (e) {
          console.warn("App download failed", e);
          if (have) next[b.app_id] = have; // keep the version that works
          result.failed.push(b.app_id);
        }
      }
      for (const [id, have] of Object.entries(current)) {
        if (!next[id]) {
          await forget(have);
          result.removed.push(id);
        }
      }
      saveRecords(next);
    } finally {
      syncing = null;
    }
    return result;
  })();
  return syncing;
}

/** Remove every installed app from this device (sign-out, server change). */
export async function clearInstalled(): Promise<void> {
  for (const b of installedAll()) await forget(b);
  saveRecords({});
}

// ------------------------------------------------------------ local opening
function dataUri(type: string, buf: ArrayBuffer): string {
  let bin = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${type};base64,${btoa(bin)}`;
}

/**
 * The app's document built from the installed files, for an iframe `srcdoc`.
 * Scripts and styles are inlined (a sandboxed frame has an opaque origin, so it
 * couldn't load them from the device otherwise); fonts and images in the CSS
 * become data URIs. Anything else resolves against the versioned server URL.
 * Returns null when the app isn't installed or a file is missing.
 */
export async function localDocument(appId: string): Promise<string | null> {
  const b = installed(appId);
  if (!b) return null;
  const hit = docs.get(b.hash);
  if (hit) return hit;
  const store = await cache();
  if (!store) return null;
  const read = async (path: string) => (await store.match(fileUrl(b, path))) ?? null;
  const entry = await read(b.entry);
  if (!entry) return null;

  const base = new URL(fileUrl(b, b.entry));
  const inBundle = (url: string, from: URL) => {
    const abs = new URL(url, from);
    const root = new URL(fileUrl(b, ""));
    return abs.href.startsWith(root.href) ? abs.href.slice(root.href.length).split(/[?#]/)[0] : null;
  };

  const doc = new DOMParser().parseFromString(await entry.text(), "text/html");
  let scriptDir = base;
  for (const s of Array.from(doc.querySelectorAll<HTMLScriptElement>("script[src]"))) {
    const path = inBundle(s.getAttribute("src")!, base);
    const file = path ? await read(path) : null;
    if (!path || !file) continue;
    const inline = doc.createElement("script");
    if (s.type) inline.type = s.type;
    // "</script" inside the code would end the element early
    inline.textContent = (await file.text()).replace(/<\/script/gi, "<\\/script");
    s.replaceWith(inline);
    scriptDir = new URL(".", fileUrl(b, path));
  }
  for (const l of Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))) {
    const path = inBundle(l.getAttribute("href")!, base);
    const file = path ? await read(path) : null;
    if (!path || !file) continue;
    const cssUrl = new URL(fileUrl(b, path));
    let css = await file.text();
    const refs = Array.from(css.matchAll(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g));
    for (const [whole, , ref] of refs) {
      if (/^(data:|https?:|#)/.test(ref)) continue;
      const refPath = inBundle(ref, cssUrl);
      const asset = refPath ? await read(refPath) : null;
      if (asset) css = css.replace(whole, `url("${dataUri(asset.headers.get("Content-Type") ?? "application/octet-stream", await asset.arrayBuffer())}")`);
    }
    const style = doc.createElement("style");
    style.textContent = css;
    l.replaceWith(style);
  }
  doc.querySelectorAll('link[rel="modulepreload"]').forEach((l) => l.remove());
  // Inline module scripts resolve relative imports against the document base: point it
  // at the script's folder on the server (versioned, so cached) for lazily loaded chunks.
  const baseEl = doc.createElement("base");
  baseEl.href = scriptDir.href;
  doc.head.prepend(baseEl);

  const html = `<!doctype html>\n${doc.documentElement.outerHTML}`;
  docs.set(b.hash, html);
  return html;
}
